import { FC, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { Box } from '@mui/material';
import { DataGrid, GridPaginationModel, GridSortDirection, GridSortModel, useGridApiRef } from '@mui/x-data-grid';
import axios from 'axios';
import { PAGINATION_LIMIT } from 'utils/constants';
import { useResizeDataGridColumn } from 'utils/hooks';
import { ROUTES } from 'routes';
import { COLUMNS_CONFIG } from './config';
import styles from './index.module.scss';
import { ServicePaymentsFilters } from './Filters';
import {
	getServicePayments,
	getServicePaymentsLoadingSelector,
	getServicePaymentsSelector,
	getServicePaymentsTotalSelector,
} from 'store/service-payments';
import toastService from 'components/elements/Toastify';
import {
	convertCoinsToUAH,
	formatQueryString,
	formatServicePaymentsReqBody,
	getErrorMessage,
	getServicePaymentsAmountSumFromQuery,
	getServicePaymentsMainBillFromQuery,
	getServicePaymentsProvidersFromQuery,
	getServicePaymentsServicesFromQuery,
	getServicePaymentsStatusFromQuery,
	parseQueryString,
} from 'utils/helpers';
import dayjs from 'dayjs';
import { PAYMENT_STATUS, PROVIDER_TYPE, REPORT_PROVIDER, REPORT_TYPE, SERVICE_PAYMENTS_FIELD_NAME, SORT_DIR } from 'utils/enums';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as Download } from 'assets/icons/download.svg';
import { IGetServicePaymentsRequest, IQuery, IReport, IServicePaymentFilters } from 'types';
import { IOption } from 'components/elements';
import { getOtherReceiptsReport } from 'store/other-receipts';
import { downloadReportPdf } from '../OtherReceiptsPage/config';
import { ReportsService } from 'utils/services';

const MAX_ALLOWED_REESTR_FUA_REPORT_DAYS = 31;
const REESTR_FUA_REPORT_POLL_DELAY_MS = 3000;
const REESTR_FUA_REPORT_POLL_ATTEMPTS = 5;

const wait = (delayMs: number) => new Promise((resolve) => setTimeout(resolve, delayMs));

const getReestrFuaCsvName = (report: IReport, dateTo: string): string => {
	const reportPeriod = report.reportPeriod || dayjs(dateTo).format('YYYYMMDD');
	const sequenceNumber = report.sequenceNumber ? String(report.sequenceNumber).padStart(2, '0') : '01';

	return report.name || `reestr_FUA_${reportPeriod}_${sequenceNumber}.csv`;
};

const isReestrFuaReportForPeriod = (report: IReport, dateFrom: string, dateTo: string): boolean => {
	const reportPeriodFrom = report.reportPeriodFrom ? dayjs(report.reportPeriodFrom).format('YYYY-MM-DD') : undefined;
	const reportPeriodTo = report.reportPeriodTo ? dayjs(report.reportPeriodTo).format('YYYY-MM-DD') : undefined;

	if (reportPeriodFrom && reportPeriodTo) {
		return reportPeriodFrom === dateFrom && reportPeriodTo === dateTo;
	}

	return report.reportPeriod === dayjs(dateTo).format('YYYYMMDD');
};

const getReestrFuaReport = async (dateFrom: string, dateTo: string): Promise<IReport | undefined> => {
	const { data } = await ReportsService.getPaymentReports({
		offset: 0,
		limit: 10,
		searchBy: {
			report_type: REPORT_TYPE.REESTR_FUA,
			provider_type: REPORT_PROVIDER.FUA,
		},
		orderBy: [{ field: 'createdAt', direction: SORT_DIR.DSC }],
	});

	return data.list.find((report) => report.s3URL && isReestrFuaReportForPeriod(report, dateFrom, dateTo));
};

const downloadReestrFuaCsv = async (report: IReport, dateTo: string) => {
	const { data } = await axios.get(report.s3URL, { responseType: 'blob' });
	const url = window.URL.createObjectURL(data);
	const downloadLink = document.createElement('a');

	downloadLink.href = url;
	downloadLink.download = getReestrFuaCsvName(report, dateTo);
	downloadLink.click();
	window.URL.revokeObjectURL(url);
};

export const ServicePaymentsPage: FC = () => {
	const apiRef = useGridApiRef();
	const dispatch = useAppDispatch();
	const isLoading = useAppSelector(getServicePaymentsLoadingSelector);
	const servicePayments = useAppSelector(getServicePaymentsSelector);
	const servicePaymentsTotal = useAppSelector(getServicePaymentsTotalSelector);
	const { search } = useLocation();
	const navigate = useNavigate();
	const [isExportCsvLoading, setIsExportCsvLoading] = useState(false);

	const query = useMemo(() => {
		return parseQueryString<IQuery>(search, { parseBooleans: true, parseNumbers: true });
	}, [search]);

	const requestBody = useMemo(() => {
		const {
			page = 0,
			pageSize = PAGINATION_LIMIT.SMALL,
			field = SERVICE_PAYMENTS_FIELD_NAME.CREATED_AT,
			sort = SORT_DIR.DSC,
			// gets date for the last 30 days
			gte = dayjs().subtract(30, 'day').startOf('day').format(),
			lte = dayjs().endOf('day').format(),
			status,
			providerService,
			searchQuery,
			services,
			amountFrom,
			amountTo,
			providerId,
			serviceId,
		} = query;

		return {
			offset: Number(pageSize) * Number(page),
			limit: Number(pageSize),
			order: {
				field,
				direction: sort as SORT_DIR,
			},
			options: {
				dateRange: {
					gte,
					lte,
				},
				serviceId,
				mainBillId: getServicePaymentsMainBillFromQuery(query.mainBillId),
				services: getServicePaymentsServicesFromQuery(services),
				provider: getServicePaymentsProvidersFromQuery(providerService, providerId),
				amount: getServicePaymentsAmountSumFromQuery(amountFrom, amountTo),
				status: getServicePaymentsStatusFromQuery(status),
				search: searchQuery?.toString(),
			},
		};
	}, [query]);

	const initialFilters = useMemo(
		() => ({
			gte: dayjs(requestBody.options.dateRange.gte) || null,
			lte: dayjs(requestBody.options.dateRange.lte) || null,
			services: requestBody.options.services || [],
			status: requestBody.options.status || [],
			amountFrom: convertCoinsToUAH(Number(requestBody.options.amount?.gte)) || '',
			amountTo: convertCoinsToUAH(Number(requestBody.options.amount?.lte)) || '',
			serviceId: query.serviceId || '',
			providerId: query.providerId || '',
			mainBillId: query.mainBillId || '',
			provider: requestBody.options.provider || [],
		}),
		// we need it only on page mounting
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const handleGetServicePayments = async (reqBody: IGetServicePaymentsRequest) => {
		try {
			await dispatch(getServicePayments(reqBody)).unwrap();
		} catch (error) {
			toastService.error(getErrorMessage(error));
		}
	};

	const handleChangePage = ({ page, pageSize }: GridPaginationModel): void => {
		navigate(
			{
				search: formatQueryString({
					...query,
					page: page || undefined,
					pageSize,
				}),
			},
			{ replace: true }
		);
	};

	const handleClickOnCell = async (params: any, event: any) => {
		if (params.field === 'download') {
			event.preventDefault();
			try {
				if (params.row.provider === PROVIDER_TYPE.HOSTPAY && params.row.status === PAYMENT_STATUS.PAID) {
					const report = await dispatch(getOtherReceiptsReport(params.row.id)).unwrap();
					downloadReportPdf(report);
				} else {
					return;
				}
			} catch (error) {
				toastService.error('Failed to download the report');
			}
			return;
		}
	};

	const handleSortTable = (model: GridSortModel): void => {
		const { field, sort } = model[0] || {};
		navigate(
			{
				search: formatQueryString({
					...query,
					field,
					sort,
				}),
			},
			{ replace: true }
		);
	};

	const handleExportCsv = async (filters: IServicePaymentFilters) => {
		const { gte, lte } = filters;

		if (!gte || !lte) {
			toastService.error('Оберіть період для завантаження CSV');
			return;
		}

		const dateFromDay = dayjs(gte).startOf('day');
		const dateToDay = dayjs(lte).startOf('day');

		if (dateToDay.isBefore(dateFromDay)) {
			toastService.error('Дата завершення не може бути меншою за дату початку');
			return;
		}

		if (dateToDay.diff(dateFromDay, 'day') >= MAX_ALLOWED_REESTR_FUA_REPORT_DAYS) {
			toastService.error(`Максимальний проміжок часу ${MAX_ALLOWED_REESTR_FUA_REPORT_DAYS} день`);
			return;
		}

		const dateFrom = dateFromDay.format('YYYY-MM-DD');
		const dateTo = dateToDay.format('YYYY-MM-DD');

		try {
			setIsExportCsvLoading(true);
			await ReportsService.generateReestrFuaReport({ dateFrom, dateTo });

			for (let attempt = 0; attempt < REESTR_FUA_REPORT_POLL_ATTEMPTS; attempt += 1) {
				await wait(REESTR_FUA_REPORT_POLL_DELAY_MS);

				const report = await getReestrFuaReport(dateFrom, dateTo);

				if (report) {
					await downloadReestrFuaCsv(report, dateTo);
					toastService.success('CSV звіт завантажено');
					return;
				}
			}

			toastService.warning('CSV звіт ще формується. Спробуйте завантажити ще раз за кілька секунд');
		} catch (error) {
			toastService.error(getErrorMessage(error));
		} finally {
			setIsExportCsvLoading(false);
		}
	};

	useEffect(() => {
		handleGetServicePayments(formatServicePaymentsReqBody(requestBody));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [requestBody]);

	const { columns, handleColumnResize } = useResizeDataGridColumn(ROUTES.servicePayments, COLUMNS_CONFIG);

	const handleChangeFilters = (filters: IServicePaymentFilters) => {
		const {
			lte,
			gte,
			services = [],
			provider,
			amountFrom,
			amountTo,
			providerId,
			serviceId,
			mainBillId,
			status,
			searchQuery,
		} = filters;

		const newSearch = formatQueryString({
			...query,
			page: undefined,
			gte: dayjs(gte).startOf('day').format(),
			lte: dayjs(lte).endOf('day').format(),
			providerService: provider,
			services: services.map((value: IOption) => JSON.stringify(value)),
			amountFrom,
			amountTo,
			providerId,
			serviceId,
			mainBillId,
			status,
			// in order to reset search input in header on click Reset button
			searchQuery: typeof searchQuery === 'string' ? undefined : query.searchQuery,
		});

		const isEqual = newSearch === search;

		if (isEqual) {
			handleGetServicePayments(formatServicePaymentsReqBody(requestBody));
			return;
		}

		navigate({ search: newSearch }, { replace: true });
	};

	return (
		<Box className={styles.tableWrapper}>
			<ServicePaymentsFilters
				initialValues={initialFilters}
				isExportCsvLoading={isExportCsvLoading}
				onExportCsv={handleExportCsv}
				onResetFilters={handleChangeFilters}
				onSubmitFilters={handleChangeFilters}
			/>
			<DataGrid
				initialState={{
					columns: {
						columnVisibilityModel: {},
					},
					pagination: {
						paginationModel: { page: query.page || 0, pageSize: query.pageSize || PAGINATION_LIMIT.SMALL },
					},
					sorting: {
						sortModel: [
							{
								field: query.field || SERVICE_PAYMENTS_FIELD_NAME.CREATED_AT,
								sort: query.sort as GridSortDirection,
							},
						],
					},
				}}
				slotProps={{
					pagination: {
						labelRowsPerPage: 'Показувати :',
					},
				}}
				loading={isLoading}
				onColumnResize={handleColumnResize}
				disableRowSelectionOnClick
				apiRef={apiRef}
				disableColumnFilter
				rows={servicePayments}
				rowCount={servicePaymentsTotal}
				onCellClick={(params, event) => handleClickOnCell(params, event)}
				columns={[
					...columns,
					{
						field: 'download',
						headerName: ' ',
						sortable: false,
						align: 'center',
						width: 60,
						renderCell: (params) => {
							const isDownload =
								params.row.provider === PROVIDER_TYPE.HOSTPAY && params.row.status === PAYMENT_STATUS.PAID;
							return isDownload ? <Download style={{ height: '100%' }} /> : null;
						},
					},
				]}
				paginationMode="server"
				sortingMode="server"
				onSortModelChange={handleSortTable}
				onPaginationModelChange={handleChangePage}
				pageSizeOptions={[PAGINATION_LIMIT.SMALL, PAGINATION_LIMIT.MIDDLE, PAGINATION_LIMIT.BIG]}
			/>
		</Box>
	);
};
